import React, {
  useState, useEffect, useContext, useRef, useCallback, useReducer
} from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Paper, Typography, List, ListItem, ListItemText, ListItemAvatar,
  Avatar, Badge, Divider, TextField, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  CircularProgress
} from "@material-ui/core";
import SendIcon from "@material-ui/icons/Send";
import PersonAddIcon from "@material-ui/icons/PersonAdd";
import GroupIcon from "@material-ui/icons/Group";
import PersonIcon from "@material-ui/icons/Person";
import DoneIcon from "@material-ui/icons/Done";
import DoneAllIcon from "@material-ui/icons/DoneAll";
import { formatDistanceToNow, format } from "date-fns";
import { ptBR } from "date-fns/locale";

import api from "../../services/api";
import openSocket from "../../services/socket-io";
import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(theme => ({
  root: {
    display: "flex",
    height: "calc(100vh - 48px)",
    overflow: "hidden",
    backgroundColor: theme.palette.background.default
  },
  sidebar: {
    width: 300,
    minWidth: 300,
    display: "flex",
    flexDirection: "column",
    borderRight: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper
  },
  sidebarHeader: {
    padding: "12px 16px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    borderBottom: `1px solid ${theme.palette.divider}`
  },
  sidebarTitle: { fontWeight: 700, fontSize: "1rem" },
  sidebarScroll: { flex: 1, overflowY: "auto" },
  sectionLabel: {
    padding: "8px 16px 4px",
    fontSize: "0.7rem",
    fontWeight: 700,
    letterSpacing: 1,
    color: theme.palette.text.secondary,
    textTransform: "uppercase"
  },
  chatItem: {
    cursor: "pointer",
    paddingLeft: 12,
    paddingRight: 12,
    "&:hover": { backgroundColor: theme.palette.action.hover }
  },
  chatItemActive: { backgroundColor: theme.palette.action.selected },
  chatPreview: {
    fontSize: "0.78rem",
    color: theme.palette.text.secondary,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: 180
  },
  chatName: { fontWeight: 600, fontSize: "0.88rem", lineHeight: 1.3 },
  main: { flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" },
  chatHeader: {
    padding: "10px 16px",
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    display: "flex",
    alignItems: "center",
    gap: 10,
    minHeight: 52
  },
  headerOnline: {
    fontSize: "0.7rem",
    color: "#44b700",
    fontWeight: 600
  },
  headerOffline: {
    fontSize: "0.7rem",
    color: theme.palette.text.disabled
  },
  messagesArea: {
    flex: 1,
    overflowY: "auto",
    padding: "12px 16px",
    display: "flex",
    flexDirection: "column",
    gap: 6
  },
  msgWrap: { display: "flex", flexDirection: "column" },
  msgWrapMe: { alignItems: "flex-end" },
  msgWrapOther: { alignItems: "flex-start" },
  bubble: {
    maxWidth: "70%",
    padding: "8px 12px",
    borderRadius: 12,
    wordBreak: "break-word"
  },
  bubbleMe: {
    backgroundColor: theme.palette.primary.main,
    color: "#fff",
    borderBottomRightRadius: 2
  },
  bubbleOther: {
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderBottomLeftRadius: 2
  },
  senderName: {
    fontSize: "0.7rem",
    fontWeight: 600,
    marginBottom: 2,
    color: theme.palette.text.secondary
  },
  msgMeta: {
    fontSize: "0.65rem",
    color: theme.palette.text.disabled,
    marginTop: 2,
    display: "flex",
    alignItems: "center",
    gap: 3
  },
  checkRead: { fontSize: 14, color: "#4fc3f7" },
  checkSent: { fontSize: 14, color: "rgba(150,150,150,0.8)" },
  inputArea: {
    padding: "8px 12px",
    borderTop: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    display: "flex",
    alignItems: "flex-end",
    gap: 8
  },
  inputField: { flex: 1 },
  empty: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    color: theme.palette.text.secondary,
    gap: 8
  },
  groupDot: {
    width: 8, height: 8, borderRadius: "50%",
    display: "inline-block", marginRight: 6
  },
  // badge dot de presença online
  presenceBadge: {
    backgroundColor: "#44b700",
    width: 10,
    height: 10,
    minWidth: 10,
    borderRadius: "50%",
    border: "2px solid white",
    bottom: 2,
    right: 2,
    top: "auto",
    transform: "none"
  }
}));

const msgReducer = (state, action) => {
  switch (action.type) {
    case "SET": return action.payload;
    case "PREPEND": return [...action.payload, ...state];
    case "ADD": {
      if (state.find(m => m.id === action.payload.id)) return state;
      return [...state, action.payload];
    }
    default: return state;
  }
};

const fmtTime = d => {
  const date = new Date(d);
  const dist = formatDistanceToNow(date, { locale: ptBR, addSuffix: true });
  return `${format(date, "HH:mm")} (${dist})`;
};

// ─── Modal: nova conversa direta ─────────────────────────────────────────────

const NewDirectModal = ({ open, onClose, onSelect, onlineUsers }) => {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(false);
  const classes = useStyles();

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get("/internal-chats/agents")
      .then(r => setAgents(r.data))
      .catch(toastError)
      .finally(() => setLoading(false));
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Nova conversa direta</DialogTitle>
      <DialogContent dividers style={{ padding: 0 }}>
        {loading
          ? <CircularProgress size={24} style={{ margin: 16 }} />
          : <List dense>
              {agents.map(a => {
                const isOnline = onlineUsers.has(Number(a.id));
                return (
                  <ListItem button key={a.id} onClick={() => onSelect(a)}>
                    <ListItemAvatar>
                      <Badge
                        overlap="circle"
                        variant="dot"
                        invisible={!isOnline}
                        classes={{ badge: classes.presenceBadge }}
                        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                      >
                        <Avatar style={{ width: 32, height: 32, fontSize: "0.8rem" }}>
                          {a.name[0].toUpperCase()}
                        </Avatar>
                      </Badge>
                    </ListItemAvatar>
                    <ListItemText
                      primary={a.name}
                      secondary={
                        <span style={{ color: isOnline ? "#44b700" : undefined }}>
                          {isOnline ? "Online" : "Offline"} · {a.profile === "admin" ? "Admin" : "Agente"}
                        </span>
                      }
                    />
                  </ListItem>
                );
              })}
            </List>
        }
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Componente principal ─────────────────────────────────────────────────────

const InternalChat = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const myId = Number(user.id);

  const [chats, setChats] = useState([]);
  const [activeChat, setActiveChat] = useState(null);
  const [messages, dispatchMsg] = useReducer(msgReducer, []);
  const [text, setText] = useState("");
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(1);
  const [newDirectOpen, setNewDirectOpen] = useState(false);

  // presença online: Set<number> de userIds online
  const [onlineUsers, setOnlineUsers] = useState(new Set());

  // mapa de leitura: { [chatId]: { [userId]: isoString | null } }
  const [readMap, setReadMap] = useState({});

  const bottomRef = useRef();
  const socketRef = useRef(null);
  const activeChatRef = useRef(null);

  useEffect(() => { activeChatRef.current = activeChat; }, [activeChat]);

  // ── carrega lista de chats ──
  const loadChats = useCallback(async () => {
    try {
      const { data } = await api.get("/internal-chats");
      setChats(data);
    } catch (err) {
      toastError(err);
    }
  }, []);

  useEffect(() => { loadChats(); }, [loadChats]);

  // ── socket: presença e mensagens ──
  useEffect(() => {
    const socket = openSocket();
    socketRef.current = socket;

    // busca estado inicial de usuários online
    socket.emit("getOnlineUsers", (ids) => {
      setOnlineUsers(new Set(ids.map(Number)));
    });

    socket.on("userOnline", ({ userId }) => {
      setOnlineUsers(prev => new Set([...prev, Number(userId)]));
    });

    socket.on("userOffline", ({ userId }) => {
      setOnlineUsers(prev => {
        const next = new Set(prev);
        next.delete(Number(userId));
        return next;
      });
    });

    socket.on("internalMessage", data => {
      if (data.action !== "create") return;
      // Atualiza lista (preview + badge)
      setChats(prev => prev.map(c =>
        c.id === data.chatId
          ? {
              ...c,
              lastMessage: {
                body: data.message.body,
                senderName: data.message.sender?.name,
                createdAt: data.message.createdAt
              },
              unread: data.message.userId !== myId &&
                      activeChatRef.current?.id !== data.chatId
                ? (c.unread || 0) + 1
                : c.unread
            }
          : c
      ));
      // Adiciona na área de mensagens se o chat está aberto
      if (activeChatRef.current?.id === data.chatId) {
        dispatchMsg({ type: "ADD", payload: data.message });
        // auto-marca como lido se sou eu quem recebe
        if (data.message.userId !== myId) {
          api.put(`/internal-chats/${data.chatId}/read`).catch(() => {});
        }
      }
    });

    socket.on("internalChatRead", ({ chatId, userId, lastReadAt }) => {
      setReadMap(prev => ({
        ...prev,
        [chatId]: {
          ...(prev[chatId] || {}),
          [Number(userId)]: lastReadAt
        }
      }));
    });

    return () => { socket.disconnect(); };
  }, [myId]);

  // ── abre um chat ──
  const openChat = useCallback(async (chat) => {
    if (socketRef.current && activeChatRef.current) {
      socketRef.current.emit("leaveInternalChat", activeChatRef.current.id);
    }

    setActiveChat(chat);
    dispatchMsg({ type: "SET", payload: [] });
    setPage(1);
    setHasMore(false);
    setLoadingMsgs(true);

    try {
      const { data } = await api.get(`/internal-chats/${chat.id}/messages?pageNumber=1`);
      dispatchMsg({ type: "SET", payload: data.messages });
      setHasMore(data.hasMore);

      // inicializa readMap com participants da resposta
      if (data.participants) {
        const participantMap = {};
        data.participants.forEach(p => {
          participantMap[Number(p.userId)] = p.lastReadAt || null;
        });
        setReadMap(prev => ({ ...prev, [chat.id]: participantMap }));
      }

      setChats(prev => prev.map(c => c.id === chat.id ? { ...c, unread: 0 } : c));
    } catch (err) {
      toastError(err);
    } finally {
      setLoadingMsgs(false);
    }

    if (socketRef.current) {
      socketRef.current.emit("joinInternalChat", chat.id);
    }
  }, []);

  // scroll para baixo quando novas mensagens chegam
  useEffect(() => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  // ── carrega mais mensagens ──
  const loadMore = useCallback(async () => {
    if (!activeChat || loadingMsgs || !hasMore) return;
    const nextPage = page + 1;
    setLoadingMsgs(true);
    try {
      const { data } = await api.get(
        `/internal-chats/${activeChat.id}/messages?pageNumber=${nextPage}`
      );
      dispatchMsg({ type: "PREPEND", payload: data.messages });
      setHasMore(data.hasMore);
      setPage(nextPage);
    } catch (err) {
      toastError(err);
    } finally {
      setLoadingMsgs(false);
    }
  }, [activeChat, loadingMsgs, hasMore, page]);

  const handleScroll = useCallback(e => {
    if (e.currentTarget.scrollTop === 0) loadMore();
  }, [loadMore]);

  // ── envia mensagem ──
  const handleSend = useCallback(async () => {
    if (!text.trim() || !activeChat) return;
    const body = text.trim();
    setText("");
    try {
      await api.post(`/internal-chats/${activeChat.id}/messages`, { body });
    } catch (err) {
      toastError(err);
    }
  }, [text, activeChat]);

  const handleKeyDown = useCallback(e => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }, [handleSend]);

  // ── iniciar conversa direta ──
  const handleSelectAgent = useCallback(async (agent) => {
    setNewDirectOpen(false);
    try {
      const { data } = await api.post("/internal-chats/direct", { userId: agent.id });
      await loadChats();
      openChat(data);
    } catch (err) {
      toastError(err);
    }
  }, [loadChats, openChat]);

  // ── abre chat de grupo de uma fila ──
  const handleOpenGroup = useCallback(async (queue) => {
    try {
      const { data } = await api.get(`/internal-chats/group/${queue.id}`);
      await loadChats();
      openChat(data);
    } catch (err) {
      toastError(err);
    }
  }, [loadChats, openChat]);

  // ── helpers ──
  const getChatName = useCallback(chat => {
    if (chat.type === "group") return chat.queue?.name || "Equipe";
    const other = (chat.participants || []).find(p => p.userId !== myId);
    return other?.user?.name || "Chat";
  }, [myId]);

  const getChatInitial = useCallback(chat => {
    return getChatName(chat)[0]?.toUpperCase() || "?";
  }, [getChatName]);

  const getOtherUserId = useCallback(chat => {
    if (chat.type !== "direct") return null;
    const other = (chat.participants || []).find(p => p.userId !== myId);
    return other ? Number(other.userId) : null;
  }, [myId]);

  // verifica se uma mensagem foi lida pelos outros participantes
  const isMessageRead = useCallback((msg, chatId) => {
    if (Number(msg.userId) !== myId) return false;
    const chatReads = readMap[chatId] || {};
    const msgDate = new Date(msg.createdAt);
    return Object.entries(chatReads).some(([uid, lr]) => {
      if (Number(uid) === myId) return false;
      if (!lr) return false;
      return new Date(lr) >= msgDate;
    });
  }, [readMap, myId]);

  const directChats = chats.filter(c => c.type === "direct");
  const groupChats = chats.filter(c => c.type === "group");

  const myQueues = (user.queues || []).filter(q => !q.isGroupQueue);
  const existingGroupQueueIds = new Set(groupChats.map(c => c.queue?.id).filter(Boolean));
  const queuesWithoutChat = myQueues.filter(q => !existingGroupQueueIds.has(q.id));

  // status do outro participante no chat direto ativo
  const activeChatOtherUserId = activeChat ? getOtherUserId(activeChat) : null;
  const activeChatOtherOnline = activeChatOtherUserId && onlineUsers.has(activeChatOtherUserId);

  return (
    <div className={classes.root}>
      {/* ── Sidebar ─────────────────────────────── */}
      <div className={classes.sidebar}>
        <div className={classes.sidebarHeader}>
          <Typography className={classes.sidebarTitle}>Chat Interno</Typography>
          <Tooltip title="Nova conversa direta">
            <IconButton size="small" onClick={() => setNewDirectOpen(true)}>
              <PersonAddIcon />
            </IconButton>
          </Tooltip>
        </div>

        <div className={classes.sidebarScroll}>
          {/* Chats de equipe */}
          {(groupChats.length > 0 || queuesWithoutChat.length > 0) && (
            <>
              <Typography className={classes.sectionLabel}>Equipes</Typography>
              {groupChats.map(chat => (
                <ListItem
                  key={chat.id}
                  className={`${classes.chatItem} ${activeChat?.id === chat.id ? classes.chatItemActive : ""}`}
                  onClick={() => openChat(chat)}
                >
                  <ListItemAvatar>
                    <Badge badgeContent={chat.unread || 0} color="secondary" max={99}>
                      <Avatar style={{ backgroundColor: chat.queue?.color || "#9e9e9e", width: 34, height: 34 }}>
                        <GroupIcon style={{ fontSize: 18 }} />
                      </Avatar>
                    </Badge>
                  </ListItemAvatar>
                  <ListItemText
                    primary={<span className={classes.chatName}>{getChatName(chat)}</span>}
                    secondary={
                      <span className={classes.chatPreview}>
                        {chat.lastMessage
                          ? `${chat.lastMessage.senderName || "?"}: ${chat.lastMessage.body}`
                          : "Sem mensagens ainda"}
                      </span>
                    }
                  />
                </ListItem>
              ))}
              {queuesWithoutChat.map(q => (
                <ListItem
                  key={`q-${q.id}`}
                  className={classes.chatItem}
                  onClick={() => handleOpenGroup(q)}
                >
                  <ListItemAvatar>
                    <Avatar style={{ backgroundColor: q.color || "#9e9e9e", width: 34, height: 34 }}>
                      <GroupIcon style={{ fontSize: 18 }} />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={<span className={classes.chatName}>{q.name}</span>}
                    secondary={<span className={classes.chatPreview}>Abrir chat da equipe</span>}
                  />
                </ListItem>
              ))}
              <Divider />
            </>
          )}

          {/* Conversas diretas */}
          <Typography className={classes.sectionLabel}>Conversas diretas</Typography>
          {directChats.length === 0 && (
            <Typography style={{ padding: "8px 16px", fontSize: "0.8rem", opacity: 0.5 }}>
              Nenhuma conversa ainda
            </Typography>
          )}
          {directChats.map(chat => {
            const otherId = getOtherUserId(chat);
            const isOnline = otherId && onlineUsers.has(otherId);
            return (
              <ListItem
                key={chat.id}
                className={`${classes.chatItem} ${activeChat?.id === chat.id ? classes.chatItemActive : ""}`}
                onClick={() => openChat(chat)}
              >
                <ListItemAvatar>
                  <Badge badgeContent={chat.unread || 0} color="secondary" max={99}>
                    <Badge
                      overlap="circle"
                      variant="dot"
                      invisible={!isOnline}
                      classes={{ badge: classes.presenceBadge }}
                      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    >
                      <Avatar style={{ width: 34, height: 34, fontSize: "0.85rem" }}>
                        {getChatInitial(chat)}
                      </Avatar>
                    </Badge>
                  </Badge>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <span className={classes.chatName}>{getChatName(chat)}</span>
                  }
                  secondary={
                    <span className={classes.chatPreview}>
                      {chat.lastMessage ? chat.lastMessage.body : "Sem mensagens ainda"}
                    </span>
                  }
                />
              </ListItem>
            );
          })}
        </div>
      </div>

      {/* ── Área de mensagens ────────────────────── */}
      {!activeChat
        ? (
          <div className={classes.empty}>
            <PersonIcon style={{ fontSize: 56, opacity: 0.3 }} />
            <Typography style={{ opacity: 0.5 }}>
              Selecione uma conversa ou inicie uma nova
            </Typography>
          </div>
        )
        : (
          <div className={classes.main}>
            {/* Header */}
            <div className={classes.chatHeader}>
              {activeChat.type === "group"
                ? <GroupIcon color="primary" />
                : (
                  <Badge
                    overlap="circle"
                    variant="dot"
                    invisible={!activeChatOtherOnline}
                    classes={{ badge: classes.presenceBadge }}
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  >
                    <PersonIcon color="action" />
                  </Badge>
                )
              }
              <div>
                <Typography style={{ fontWeight: 700, fontSize: "0.95rem", lineHeight: 1.2 }}>
                  {getChatName(activeChat)}
                </Typography>
                {activeChat.type === "direct" && (
                  <Typography className={activeChatOtherOnline ? classes.headerOnline : classes.headerOffline}>
                    {activeChatOtherOnline ? "Online" : "Offline"}
                  </Typography>
                )}
                {activeChat.type === "group" && (
                  <Typography variant="caption" color="textSecondary">
                    Chat da equipe · {(activeChat.participants || []).length} membro(s)
                  </Typography>
                )}
              </div>
              {activeChat.type === "group" && activeChat.queue && (
                <span
                  className={classes.groupDot}
                  style={{ backgroundColor: activeChat.queue.color || "#9e9e9e" }}
                />
              )}
            </div>

            {/* Mensagens */}
            <div className={classes.messagesArea} onScroll={handleScroll}>
              {loadingMsgs && page === 1 && (
                <CircularProgress size={20} style={{ alignSelf: "center", margin: 8 }} />
              )}
              {hasMore && !loadingMsgs && (
                <Typography
                  variant="caption"
                  align="center"
                  style={{ cursor: "pointer", color: "#1976d2" }}
                  onClick={loadMore}
                >
                  Carregar mensagens anteriores
                </Typography>
              )}
              {messages.map(msg => {
                const isMe = Number(msg.userId) === myId;
                const read = isMe && isMessageRead(msg, activeChat.id);
                return (
                  <div
                    key={msg.id}
                    className={`${classes.msgWrap} ${isMe ? classes.msgWrapMe : classes.msgWrapOther}`}
                  >
                    {!isMe && activeChat.type === "group" && (
                      <Typography className={classes.senderName}>
                        {msg.sender?.name || "?"}
                      </Typography>
                    )}
                    <Paper
                      elevation={0}
                      className={`${classes.bubble} ${isMe ? classes.bubbleMe : classes.bubbleOther}`}
                    >
                      {msg.body}
                    </Paper>
                    <div className={classes.msgMeta}>
                      <span>{fmtTime(msg.createdAt)}</span>
                      {isMe && (
                        read
                          ? <DoneAllIcon className={classes.checkRead} />
                          : <DoneIcon className={classes.checkSent} />
                      )}
                    </div>
                  </div>
                );
              })}
              <div ref={bottomRef} />
            </div>

            {/* Input */}
            <div className={classes.inputArea}>
              <TextField
                className={classes.inputField}
                variant="outlined"
                size="small"
                multiline
                maxRows={4}
                placeholder="Digite uma mensagem... (Enter para enviar)"
                value={text}
                onChange={e => setText(e.target.value)}
                onKeyDown={handleKeyDown}
              />
              <IconButton
                color="primary"
                disabled={!text.trim()}
                onClick={handleSend}
              >
                <SendIcon />
              </IconButton>
            </div>
          </div>
        )
      }

      <NewDirectModal
        open={newDirectOpen}
        onClose={() => setNewDirectOpen(false)}
        onSelect={handleSelectAgent}
        onlineUsers={onlineUsers}
      />
    </div>
  );
};

export default InternalChat;
